import { ROUTES, buildCurrentUserProfilePath } from "@/constants/routes";
import { useAuthContext } from "@/context/auth/useAuthContext";
import { getCurrentAuthUser } from "@/api/auth";
import { getUserDataByUserId } from "@/api/user";
import useEffectivePathname from "@/hooks/useEffectivePathname";

const MOBILE_NAV_ICON_PATHS = {
  play: "/icons/mobile-nav/cicle-play.svg",
  place: "/icons/mobile-nav/map-pin-search.svg",
  create: "/icons/mobile-nav/plus.svg",
  notifications: "/icons/mobile-nav/bell.svg",
  profile: "/icons/mobile-nav/user.svg",
};

const MaskedIcon = ({ src, className = "" }) => (
  <span
    className={`block bg-current ${className}`}
    aria-hidden="true"
    style={{
      WebkitMask: `url(${src}) center / contain no-repeat`,
      mask: `url(${src}) center / contain no-repeat`,
    }}
  />
);

const UL_BottomBar = ({ routeTo }) => {
  const pathname = useEffectivePathname();
  const { user, setIsLoginModalOpen, totalUnreadNotifications } =
    useAuthContext();

  const profilePath = buildCurrentUserProfilePath(user?.username, user?.userId);
  const tabs = [
    {
      id: "play",
      title: "플레이",
      icon: MOBILE_NAV_ICON_PATHS.play,
      link: ROUTES.current.play,
      isActive: pathname === ROUTES.current.play,
    },
    {
      id: "place",
      title: "플레이스",
      icon: MOBILE_NAV_ICON_PATHS.place,
      link: ROUTES.current.place,
      isActive: pathname?.startsWith(ROUTES.current.place),
    },
    {
      id: "notifications",
      title: "알림",
      icon: MOBILE_NAV_ICON_PATHS.notifications,
      link: ROUTES.user.notifications,
      protected: true,
      badge: totalUnreadNotifications,
      isActive: pathname?.startsWith(ROUTES.user.notifications),
    },
    {
      id: "profile",
      title: "프로필",
      icon: MOBILE_NAV_ICON_PATHS.profile,
      link: profilePath,
      protected: true,
      isActive: Boolean(profilePath && pathname === profilePath),
    },
  ];

  const handleNavigate = (tab) => {
    if (tab?.protected && !user?.userId) {
      setIsLoginModalOpen(true);
      return;
    }

    if (tab?.id === "profile") {
      routeCurrentUserProfile();
      return;
    }

    if (tab?.link) routeTo(tab.link);
  };

  const routeCurrentUserProfile = async () => {
    if (!user?.userId) {
      setIsLoginModalOpen(true);
      return;
    }

    let target = buildCurrentUserProfilePath(null, user.userId);

    try {
      const currentAuth = await getCurrentAuthUser();
      if (currentAuth?.token) {
        localStorage.setItem("jwtToken", currentAuth.token);
      }

      const latestUser = currentAuth?.user;
      target =
        buildCurrentUserProfilePath(
          latestUser?.username,
          latestUser?.userId || latestUser?.id || user.userId
        ) || target;
    } catch {
      try {
        const latestUser = await getUserDataByUserId(user.userId, { page: 1 });
        target =
          buildCurrentUserProfilePath(
            latestUser?.username,
            latestUser?.userId || latestUser?.id || user.userId
          ) || target;
      } catch {
        target = buildCurrentUserProfilePath(null, user.userId);
      }
    }

    routeTo(target);
  };

  const handleCreate = () => {
    if (!user?.userId) {
      setIsLoginModalOpen(true);
      return;
    }

    routeTo(ROUTES.creator.upload);
  };

  // 콘텐츠 하나를 보는 화면(/play/<id>, /post/<id>)에서는 하단 탭바를 감춘다.
  // 댓글 입력줄과 탭바가 위아래로 겹쳐 손가락 자리를 두 줄 차지했다(2026-07-31 검수 4-A).
  const isContentDetailPage = /^\/(play|post)\/[^/]+/.test(pathname || "");
  if (isContentDetailPage) return null;

  return (
    <nav className="fixed inset-x-0 bottom-0 z-[95] md:hidden">
      <div className="relative h-[52px] rounded-t-[20px] border border-black/5 bg-white px-4 shadow-[0_-6px_18px_rgba(15,23,42,0.12)] dark:border-white/10 dark:bg-custom_black">
        <div className="grid h-full grid-cols-[1fr_1fr_56px_1fr_1fr] items-center">
          {tabs.slice(0, 2).map((tab) => (
            <MobileNavItem key={tab.id} tab={tab} onClick={handleNavigate} />
          ))}

          <div className="flex justify-center">
            <button
              type="button"
              aria-label="작성"
              className="flex h-[52px] w-[52px] items-center justify-center rounded-full bg-[#111214] text-white shadow-[0_7px_18px_rgba(0,0,0,0.24)] transition active:scale-95"
              onClick={handleCreate}
            >
              <MaskedIcon
                src={MOBILE_NAV_ICON_PATHS.create}
                className="h-7 w-7"
              />
            </button>
          </div>

          {tabs.slice(2).map((tab) => (
            <MobileNavItem key={tab.id} tab={tab} onClick={handleNavigate} />
          ))}
        </div>
      </div>
    </nav>
  );
};

const MobileNavItem = ({ tab, onClick }) => (
  <button
    type="button"
    className={`relative flex h-full min-w-0 flex-col items-center justify-center gap-0.5 rounded-[14px] px-1 text-[11px] font-medium leading-none transition active:scale-95 ${
      tab.isActive
        ? "bg-black/[0.035] text-black dark:bg-white/10 dark:text-white"
        : "text-black/58 dark:text-white/62"
    }`}
    onClick={() => onClick(tab)}
  >
    <span
      className={`relative flex h-7 w-7 items-center justify-center ${
        tab.isActive
          ? "rounded-full bg-black text-white dark:bg-white dark:text-black"
          : ""
      }`}
    >
      {!!tab.badge && tab.badge > 0 && (
        <span className="absolute -right-1 -top-1 z-10 flex h-4 min-w-4 items-center justify-center rounded-full bg-red px-1 text-[11px] font-semibold leading-none text-white">
          {tab.badge > 99 ? "99+" : tab.badge}
        </span>
      )}
      <MaskedIcon
        src={tab.icon}
        className={`${tab.id === "play" ? "h-[18px] w-[18px]" : "h-[22px] w-[22px]"}`}
      />
    </span>
    <span className="truncate">{tab.title}</span>
  </button>
);

export default UL_BottomBar;
